import { BiddingGameState, GameStateStatus, PlayerTableViewModelV2 } from 'shared';
import { Panel } from '../../ui/Panel';
import { Text } from '../../ui/Text';
import { token, zVar } from '../../ui/theme';

/*
 * CurrentAction — HUD banner (top-center) telling the player what's happening /
 * whose turn it is. DOM/Panel replacement for SvgCurrentAction; message logic
 * (getActionMessage) unchanged.
 */

interface Props {
  table: PlayerTableViewModelV2;
}

function getActionMessage(table: PlayerTableViewModelV2): {
  message: string;
  isYourTurn: boolean;
  subMessage?: string;
} {
  const { gameState, tablePlayers } = table;
  if (!gameState) return { message: 'Waiting to start', isYourTurn: false };

  const me = tablePlayers[0];
  const isMyTurn = me?.isCurrentTurn ?? false;
  const activePlayer = tablePlayers.find((p) => p?.isCurrentTurn);
  const activeName = activePlayer?.name ?? 'Waiting…';

  switch (gameState.status) {
    case GameStateStatus.Bidding: {
      const gs = gameState as BiddingGameState;
      if (!gs.currentBiddingPlayer)
        return {
          message: 'Bidding starting…',
          isYourTurn: false,
          subMessage: activeName !== 'Waiting…' ? activeName : undefined,
        };
      return isMyTurn
        ? { message: 'Your turn to bid', isYourTurn: true }
        : { message: `${activeName}'s turn to bid`, isYourTurn: false };
    }
    case GameStateStatus.StartingSet:
      return { message: 'Starting new set…', isYourTurn: false };
    case GameStateStatus.StartingTrick:
      return isMyTurn
        ? { message: 'Your turn to lead', isYourTurn: true }
        : { message: `${activeName} leads the trick`, isYourTurn: false };
    case GameStateStatus.Playing:
      return isMyTurn
        ? { message: 'Your turn to play', isYourTurn: true }
        : { message: `${activeName}'s turn to play`, isYourTurn: false };
    case GameStateStatus.ScoringTrick:
      return {
        message: `${activeName} takes the trick`,
        isYourTurn: isMyTurn,
        subMessage: isMyTurn ? 'Your turn to lead' : `${activeName} leads next`,
      };
    case GameStateStatus.ScoringSet:
      return { message: 'Set complete', isYourTurn: false };
    default:
      return { message: '', isYourTurn: false };
  }
}

export const CurrentAction: React.FC<Props> = ({ table }) => {
  if (!table.gameState) return null;

  // While paused (a player left, awaiting the host's decision) the pause modal
  // carries the detail; the banner just states the game is halted.
  const { message, isYourTurn, subMessage } = table.isPaused
    ? { message: 'Game paused', isYourTurn: false, subMessage: 'A player left' }
    : getActionMessage(table);
  if (!message) return null;

  return (
    <Panel
      variant="hud"
      role="status"
      aria-live="polite"
      aria-atomic="true"
      style={{
        position: 'absolute',
        left: '32%',
        top: 24,
        transform: 'translateX(-50%)',
        zIndex: zVar('z-hud'),
        textAlign: 'center',
        minWidth: 240,
        border: `1px solid ${isYourTurn ? token('color-gold') : token('color-border')}`,
      }}
    >
      <Text as="div" variant="value" color={isYourTurn ? token('color-gold') : token('color-text')}>
        {message}
      </Text>
      {subMessage && (
        <Text as="div" variant="muted" style={{ marginTop: 'var(--space-1)' }}>
          {subMessage}
        </Text>
      )}
    </Panel>
  );
};
